import { useEffect, useState } from "react";
import axios from "axios";
import { mloPhotoUrl } from "@/lib/photo";
import { ArrowRight, ShieldCheck, MessageSquare, Zap, Users, Award, TrendingDown, Clock3 } from "lucide-react";
import Nav from "@/components/landing/Nav";
import HowItWorks from "@/components/landing/HowItWorks";
import PrivacyTrust from "@/components/landing/PrivacyTrust";
import Footer from "@/components/landing/Footer";
import { useRateCheckRouter } from "@/components/RateCheckRouter";
import { ASSETS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PlatformHero() {
  const { openRouter } = useRateCheckRouter();
  return (
    <section
      data-testid="platform-hero"
      className="relative overflow-hidden pt-24 sm:pt-32 pb-20 sm:pb-28 grain-bg noise text-white"
      style={{ background: "linear-gradient(135deg, #08192F 0%, #0F2A47 45%, #17385e 100%)" }}
    >
      <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-teal/20 blur-3xl" aria-hidden />
      <div className="absolute -bottom-40 -right-24 h-[26rem] w-[26rem] rounded-full bg-lime/10 blur-3xl" aria-hidden />
      <div className="relative max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-lime">
            Great Rate Check
          </p>
          <h1 className="mt-4 font-heading font-medium text-4xl sm:text-6xl leading-[1.02] text-balance">
            A great mortgage answer{" "}
            <span className="italic text-lime">is a two-minute question</span> away.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-white/85 leading-relaxed max-w-2xl">
            Great Rate Check is a nationwide platform of vetted loan officers who deliver
            personalized rate scenarios, plain-English guidance, and one dedicated advocate —
            no bidding wars, no bait-and-switch.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="platform-hero-cta"
              onClick={() => openRouter("platform-hero-cta")}
              className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
            >
              Check My Rate <ArrowRight size={18} />
            </button>
            <a
              href="#network"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium underline decoration-white/30 underline-offset-4"
            >
              Meet the network
            </a>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-white/70 text-sm">
            <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-lime" /> Personal · confidential</span>
            <span className="inline-flex items-center gap-2"><Clock3 size={16} className="text-lime" /> ~2-minute rate check</span>
            <span className="inline-flex items-center gap-2"><TrendingDown size={16} className="text-lime" /> Competitive lender network</span>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative rounded-3xl bg-white/5 border border-white/10 backdrop-blur p-8 sm:p-10">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-lime/20 text-lime flex items-center justify-center">
                <Users size={22} />
              </div>
              <div>
                <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-lime">The Great Rate Check network</p>
                <p className="font-heading text-xl text-white">Real loan officers. Real relationships.</p>
              </div>
            </div>
            <ul className="mt-6 space-y-3 text-sm text-white/85">
              <li className="flex gap-3"><span className="text-lime shrink-0">▸</span> <span>Every rate scenario is personally reviewed by one loan officer — from start to close.</span></li>
              <li className="flex gap-3"><span className="text-lime shrink-0">▸</span> <span>Access to a national wholesale lender network through NEXA — competitive rates on every loan type.</span></li>
              <li className="flex gap-3"><span className="text-lime shrink-0">▸</span> <span>Plain-English guidance, no sales pressure, no info-selling to third parties.</span></li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function MLOCard({ m }) {
  // Uploaded photo when available; Jason's seeded record uses the bundled asset;
  // otherwise fall back to the Great Rate Check logomark for brand consistency.
  const hasPortrait = m.has_photo || m.slug === "jason";
  const photoUrl = m.has_photo ? mloPhotoUrl(m) : (m.slug === "jason" ? ASSETS.headshotApply : null);
  return (
    <a
      href={`/${m.slug}`}
      data-testid={`platform-mlo-card-${m.slug}`}
      className="group rounded-3xl bg-white border border-linen-border overflow-hidden shadow-soft hover:shadow-lift transition-all hover:-translate-y-1"
    >
      <div className="aspect-[4/5] w-full overflow-hidden bg-navy-deep">
        {hasPortrait && photoUrl ? (
          <img src={photoUrl} alt={m.name} className="w-full h-full object-cover object-top" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-deep via-[#0F2A47] to-[#17385e] p-10">
            <img
              src={ASSETS.logoMark}
              alt="Great Rate Check"
              className="w-full h-full object-contain opacity-90"
              loading="lazy"
            />
          </div>
        )}
      </div>
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-widest text-teal">Loan officer</p>
        <p className="mt-1 font-heading font-medium text-lg text-navy leading-tight">{m.name}</p>
        <p className="text-xs text-ink-500 mt-0.5">{m.title || "Mortgage Loan Officer"}</p>
        {m.nmls && <p className="text-[11px] text-ink-500 mt-1">NMLS #{m.nmls}</p>}
        <p className="mt-4 inline-flex items-center gap-1.5 text-sm text-teal font-medium group-hover:gap-2 transition-all">
          Check your rate with {m.name.split(" ")[0]} <ArrowRight size={14} />
        </p>
      </div>
    </a>
  );
}

function Network() {
  const [mlos, setMlos] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/mlos/public`);
        setMlos(res.data || []);
      } catch {
        setMlos([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section id="network" data-testid="platform-network" className="py-20 sm:py-28 bg-linen">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">Meet the network</p>
          <h2 className="mt-4 font-heading font-medium text-3xl sm:text-5xl text-balance">
            One officer. Start to close. Yours.
          </h2>
          <p className="mt-5 text-lg text-ink-600 leading-relaxed">
            Pick the loan officer who fits your market and your style. Every officer in the network is licensed, vetted, and trained to deliver the Great Rate Check experience — start to close.
          </p>
        </div>
        <div className="mt-12">
          {loading ? (
            <p className="text-sm text-ink-500">Loading network…</p>
          ) : mlos.length === 0 ? (
            <p className="text-sm text-ink-500">Network coming soon.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {mlos.slice(0, 6).map((m) => <MLOCard key={m.slug} m={m} />)}
              </div>
              {mlos.length > 6 && (
                <div className="mt-8 flex justify-center">
                  <a
                    href="/mlos"
                    data-testid="platform-browse-all-mlos"
                    className="inline-flex items-center gap-1.5 text-teal hover:text-navy font-medium text-sm underline decoration-teal/30 underline-offset-4"
                  >
                    See all {mlos.length} loan officers <ArrowRight size={14} />
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function ForLoanOfficers() {
  return (
    <section
      data-testid="platform-for-mlos"
      className="py-20 sm:py-28 text-white"
      style={{ background: "linear-gradient(135deg, #08192F 0%, #0F2A47 45%, #17385e 100%)" }}
    >
      <div className="max-w-4xl mx-auto px-6 text-center">
        <p className="text-xs font-bold tracking-[0.22em] uppercase text-lime">For loan officers</p>
        <h2 className="mt-4 font-heading font-medium text-3xl sm:text-5xl text-balance">
          Build more than a pipeline. <span className="italic text-lime">Build a growth platform.</span>
        </h2>
        <p className="mt-6 text-lg text-white/85 leading-relaxed">
          Great Rate Check is a growth platform for licensed MLOs who want systems, relationships, and BDM leverage — powered by NEXA. Your own branded page, a shared marketing library, a Realtor-partnership engine, and a national network.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href="/mloprogram"
            data-testid="platform-mlo-cta"
            className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
          >
            Learn about the MLO Growth Program <ArrowRight size={18} />
          </a>
          <a href="/mlogrowth" className="text-white/85 hover:text-white text-sm font-medium underline decoration-white/30 underline-offset-4">Skip ahead — apply now</a>
        </div>
      </div>
    </section>
  );
}

function Whys() {
  const items = [
    { icon: <MessageSquare className="w-6 h-6" />, title: "Plain-English guidance", body: "No jargon, no sales pressure. Real conversations about your real numbers." },
    { icon: <Zap className="w-6 h-6" />, title: "One officer, start to close", body: "The person you first speak with is the person who closes your loan. No handoffs." },
    { icon: <Award className="w-6 h-6" />, title: "National wholesale rates", body: "Great Rate Check officers work at NEXA Lending — access to a competitive national lender network." },
    { icon: <ShieldCheck className="w-6 h-6" />, title: "Your info stays private", body: "We don't sell your information to third parties. Ever." },
  ];
  return (
    <section data-testid="platform-whys" className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">Why Great Rate Check</p>
          <h2 className="mt-4 font-heading font-medium text-3xl sm:text-5xl text-balance">
            A better way to check your rate.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((it, i) => (
            <div key={i} className="rounded-3xl bg-linen border border-linen-border p-6">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-white text-teal border border-linen-border">
                {it.icon}
              </div>
              <h3 className="mt-4 font-heading font-medium text-xl text-navy">{it.title}</h3>
              <p className="mt-2 text-sm text-ink-600 leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function PlatformLanding() {
  return (
    <div data-testid="platform-landing" className="min-h-screen bg-linen text-navy antialiased">
      <Nav />
      <main>
        <PlatformHero />
        <Whys />
        <Network />
        <HowItWorks />
        <ForLoanOfficers />
        <PrivacyTrust />
      </main>
      <Footer />
    </div>
  );
}
